// client/src/components/NotificationsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { io } from 'socket.io-client';
import {
  Container, Box, Typography, Paper, Avatar,
  IconButton, Button, Tooltip, CircularProgress, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AppBarHeader from './AppBarHeader';
import axiosInstance from '../axiosInstance';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

function timeAgo(date) {
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const { featureFlags } = useFeatureFlags();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (featureFlags && !featureFlags.enableNotifications) {
      navigate('/');
    }
  }, [featureFlags, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/notifications');
      setNotifications(res.data);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    try {
      await axiosInstance.patch('/notifications/read-all');
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchNotifications();
      markAllAsRead();
    })();
  }, [fetchNotifications, markAllAsRead]);

  // Subscribe to real-time notifications while on this page
  useEffect(() => {
    if (!token) return;
    const socket = io(window.location.origin, {
      path: '/socket.io/',
      auth: { token },
      transports: ['polling'],
      secure: true,
      rejectUnauthorized: false,
      upgrade: false,
    });
    socket.on('notification', (notif) => {
      setNotifications(prev => [notif, ...prev]);
    });
    return () => socket.disconnect();
  }, [token]);

  const dismissOne = async (id) => {
    setNotifications(prev => {
      const next = prev.filter(n => n._id !== id);
      return next;
    });
    try {
      await axiosInstance.delete(`/notifications/${id}`);
    } catch (err) {
      console.error('Error dismissing notification:', err);
      // Restore: re-fetch to get authoritative state
      fetchNotifications();
    }
  };

  const dismissAll = async () => {
    const previous = notifications;
    setNotifications([]);
    try {
      await axiosInstance.delete('/notifications');
    } catch (err) {
      console.error('Error dismissing all notifications:', err);
      setNotifications(previous);
    }
  };

  const handleRowClick = (notif) => {
    if (!notif.link) return;
    navigate(notif.link);
  };

  if (featureFlags && !featureFlags.enableNotifications) return null;

  return (
    <>
      <AppBarHeader isAdmin={user?.isAdmin} onLogout={handleLogout} />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4" fontWeight={700}>Notifications</Typography>
          {notifications.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {notifications.some(n => !n.isRead) && (
                <Button variant="text" size="small" onClick={markAllAsRead}>
                  Mark all as read
                </Button>
              )}
              <Button variant="outlined" size="small" onClick={dismissAll}>
                Dismiss all
              </Button>
            </Box>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              You're all caught up.
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={2}>
            {notifications.map((notif, idx) => (
              <React.Fragment key={notif._id}>
                {idx > 0 && <Divider />}
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label={notif.message}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 3,
                    py: 2,
                    cursor: 'pointer',
                    bgcolor: notif.isRead ? undefined : 'rgba(25,118,210,0.07)',
                    borderLeft: notif.isRead ? '3px solid transparent' : '3px solid',
                    borderLeftColor: notif.isRead ? 'transparent' : 'primary.main',
                    '&:hover': { bgcolor: notif.isRead ? 'rgba(255,255,255,0.04)' : 'rgba(25,118,210,0.12)' },
                    transition: 'background 0.15s',
                  }}
                  onClick={() => {
                    if (!notif.isRead) {
                      setNotifications(prev => prev.map(n => n._id === notif._id ? { ...n, isRead: true } : n));
                    }
                    handleRowClick(notif);
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleRowClick(notif); }}
                >
                  <Avatar
                    src={notif.actor?.profileImage
                      ? axiosInstance.getImageUrl(notif.actor.profileImage)
                      : undefined}
                    sx={{ width: 40, height: 40, flexShrink: 0 }}
                  >
                    {notif.actor?.username?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {!notif.isRead && (
                        <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main', flexShrink: 0 }} />
                      )}
                      <Typography variant="body2" noWrap fontWeight={notif.isRead ? 400 : 600}>
                        {notif.message}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {timeAgo(notif.createdAt)}
                    </Typography>
                  </Box>
                  <Tooltip title="Dismiss">
                    <IconButton
                      size="small"
                      onClick={e => { e.stopPropagation(); dismissOne(notif._id); }}
                      aria-label="Dismiss notification"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </React.Fragment>
            ))}
          </Paper>
        )}
      </Container>
    </>
  );
}
